<template>
  <div class="khqr-container" :class="{ 'paid': isPaid }">
    <div class="khqr-card">
      <div class="khqr-header">
        <span class="khqr-title">KHQR</span>
      </div>
      
      <div class="khqr-content">
        <div class="merchant-name">
          {{ props.merchantName }}
        </div>

        <div class="amount">
          <span class="amount-value">{{ formatAmount(props.amount) }}</span>
          <span class="currency-code">{{ props.currency }}</span>
        </div>
    
        <div class="qr-code-container">
          <canvas ref="qrCanvas" class="qr-canvas" :style="{ opacity: isPaid ? 0.2 : 1 }"></canvas>
          
          <div v-if="!isPaid" class="currency-symbol-overlay">
            <div :class="['currency-symbol-container', props.currency.toLowerCase()]">
              <span class="currency-symbol">{{ props.currency === 'USD' ? '$' : '៛' }}</span>
            </div>
          </div>

          <div v-else class="paid-overlay">
            <div class="paid-badge">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
              <span>PAID</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import QRCode from 'qrcode'
import { BakongKHQR, khqrData, IndividualInfo } from 'bakong-khqr'

import axios from 'axios'
import API_URL from '@/service/apiURL'

const props = defineProps({
  merchantName: {
    type: String,
    required: true
  },
  amount: {
    type: [Number, String],
    required: true
  },
  currency: {
    type: String,
    default: 'USD',
    validator: (value) => ['USD', 'KHR'].includes(value)
  },
  merchantId: {
    type: String,
    required: true
  },
  merchantCity: {
    type: String,
    default: 'Phnom Penh'
  },
  countryCode: {
    type: String,
    default: 'KH'
  },
  billNumber: {
    type: String,
    default: '#0001'
  },
  mobileNumber: {
    type: String,
    default: ''
  },
  storeLabel: {
    type: String,
    default: ''
  },
  terminalLabel: {
    type: String,
    default: ''
  },
  expireTime: {
    type: Number,
    default: 5
  }
})

const emit = defineEmits(['success', 'error'])

const qrCanvas = ref(null)
const currentMd5 = ref(null)
const isChecking = ref(false)
const isPaid = ref(false)
const pollingInterval = ref(null)
const error = ref(null)

const formatAmount = (amount) => {
  if (typeof amount === 'string') {
    amount = parseFloat(amount)
  }
  
  if (props.currency === 'KHR') {
    return new Intl.NumberFormat('en-US').format(Math.round(amount))
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }
}

const generateQRString = () => {
  // Get expiration timestamp (Use expireTime prop or default to short duration)
  const expiryDate = new Date()
  expiryDate.setMinutes(expiryDate.getMinutes() + props.expireTime)
  const expiryTimestamp = Math.floor(expiryDate.getTime() / 1000)

  // KHQR format based on EMVCo QR Code Specification for merchant-presented QR
  const data = {
    // Payload Format Indicator
    '00': '01',
    // Point of Initiation Method (Dynamic)
    '01': '12',
    // Merchant Account Information
    '29': {
      '00': 'KH.KHQR',
      '01': props.merchantId,
    },
    // Merchant Category Code (MCC)
    '52': '5999',
    // Transaction Currency
    '53': props.currency === 'USD' ? '840' : '116',
    // Transaction Amount
    '54': props.amount.toString(),
    // Country Code
    '58': props.countryCode,
    // Merchant Name
    '59': props.merchantName,
    // Merchant City
    '60': props.merchantCity,
    // Expiry Date (YYYYMMDDHHMMSS format)
    '65': expiryDate.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14),
  }

  // Convert the data object to EMVCo QR format
  let qrString = ''
  for (const [id, value] of Object.entries(data)) {
    if (typeof value === 'object') {
      let subTLV = ''
      for (const [subId, subValue] of Object.entries(value)) {
        subTLV += subId + subValue.length.toString().padStart(2, '0') + subValue
      }
      qrString += id + subTLV.length.toString().padStart(2, '0') + subTLV
    } else {
      qrString += id + value.length.toString().padStart(2, '0') + value
    }
  }

  return qrString
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
  isChecking.value = false
}


const checkTransaction = async () => {
  console.log("currentMd5.value", currentMd5.value);

  if (!currentMd5.value || isChecking.value || isPaid.value) return;

  try {
    isChecking.value = true;

    // Call local Express API instead of Firebase function
    const response = await axios.post(`${API_URL}/bakong/checkBakongTransaction`, {
      md5: currentMd5.value,
      //token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjp7ImlkIjoiYzI4NmMwMDZkOWI4NDE0MSJ9LCJpYXQiOjE3NzAyNzk1MzksImV4cCI6MTc3ODA1NTUzOX0.ot0ASJY8uu7-haMAqctUrsFRItm62hthHb1oo6sKw30"
    });

   

    console.log("response check transaction", response.data);

    // Result from API is { success: true, result: true/false }
    if (response.data && response.data.result === true) {
      isPaid.value = true;
      stopPolling();
      emit("success", { md5: currentMd5.value , response: response.data });
    }
  } catch (err) {
    console.error("Transaction check failed:", err);
    error.value = err.response?.data?.message || err.message;
  } finally {
    isChecking.value = false;
  }
};


const startPolling = () => {
  stopPolling()
  error.value = null
  // Poll every 3 seconds
  pollingInterval.value = setInterval(checkTransaction, 3000)
}

const generateBakongQR = async () => {
  try {
    if (!qrCanvas.value) return
    stopPolling()

    // Try Bakong library first
    try {
      const optionalData = {
        currency: props.currency === 'USD' ? khqrData.currency.usd : khqrData.currency.khr,
        amount: Number(props.amount),
        billNumber: props.billNumber,
        mobileNumber: props.mobileNumber,
        storeLabel: props.storeLabel || props.merchantName,
        terminalLabel: props.terminalLabel,
        expirationTimestamp: Date.now() + (props.expireTime * 60 * 1000),
      }

      const individualInfo = new IndividualInfo(
        props.merchantId,
        props.merchantName,
        props.merchantCity,
        optionalData
      )

      const KHQR = new BakongKHQR()
      const individual = KHQR.generateIndividual(individualInfo)

      if (individual && individual.data && individual.data.qr) {
        currentMd5.value = individual.data.md5
        await QRCode.toCanvas(qrCanvas.value, individual.data.qr, {
          width: 240,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H'
        })
        
        if (currentMd5.value) {
          startPolling()
        }
        return
      }
    } catch (bakongError) {
      console.warn('Bakong QR generation failed, falling back to EMVCo format:', bakongError)
    }

    // Fallback to EMVCo format if Bakong fails
    const qrString = generateQRString()
    await QRCode.toCanvas(qrCanvas.value, qrString, {
      width: 240,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'H'
    })

  } catch (error) {
    console.error('Error generating QR code:', error)
  }
}

// Lifecycle hooks

onMounted(() => {
  generateBakongQR()
})

onUnmounted(() => {
  stopPolling()
})

watch(
  [
    () => props.amount,
    () => props.currency,
    () => props.merchantName,
    () => props.merchantId
  ],
  () => {
    generateBakongQR()
  }
)

</script>

<style>
@import '../assets/KhqrGenerator.css';
</style> 